import {
  GOOGLEMAP_API_KEY,
  LOCAL_EMAIL_RESEND_COUNT,
  MIN_DIGIT_AFTER_DECIMAL,
  regexGetTagInContent,
  regexHashTag,
  regexWeb,
} from '@/constants/Common'
import { GamesScore } from '@/types'
import imageCompression from 'browser-image-compression'
import { ContentState, convertFromHTML } from 'draft-js'
import i18n from 'i18next'
import moment from 'moment'
import momenttz from 'moment-timezone'

export const checkWebPermission = () => {
  return window.innerWidth >= 600
}

export const has = (state: string[] | null, permission: string, allowOnMobile = false) => {
  if (state && state?.includes(permission) && (allowOnMobile ? true : checkWebPermission())) {
    return true
  }
  return false
}

export const BgColor = (value: string) => {
  const s = value[0]?.toUpperCase() + value.slice(1).toLowerCase()
  if (s === 'Ongoing') return 'bg-green'
  if (s === 'Draft') return 'bg-warn'
  if (s === 'Open') return 'bg-primary-500'
  return 'bg-slate-500'
}

export const ContentColor = (value: string) => {
  const s = value[0]?.toUpperCase() + value.slice(1).toLowerCase()
  if (s === 'Ongoing') return 'text-green'
  if (s === 'Draft') return 'text-warn'
  if (s === 'Open') return 'text-primary-500'
  return 'text-slate-500'
}
export const EventStatusCase = (value: string, t: any) => {
  const s = value[0]?.toUpperCase() + value.slice(1).toLowerCase()
  if (s === 'Upcoming') return t('common.upcoming')
  if (s === 'Draft') return t('common.draft')
  if (s === 'Open') return t('common.open')
  if (s === 'Ongoing') return t('common.ongoing')
  return s
}

export const disabledInputGameScore = (obj: GamesScore, keysToCheck: [1] | [1, 2] | [1, 2, 3] | [1, 2, 3, 4]) => {
  for (const key of keysToCheck) {
    if ((obj[key].team1 && obj[key].team2) || obj[key].team1 === 0 || obj[key].team2 === 0) {
      continue
    } else {
      return true
    }
  }

  return false
}

export const imageCompresser = async (file: any) => {
  if (file === null) {
    return file
  }
  const imageFile = file?.length > 0 ? file[0] : file
  const options = {
    maxWidthOrHeight: 1024,
    maxIteration: 30,
    useWebWorker: true,
  }
  const result = await imageCompression(imageFile, options)
  const fileObject = new File([result], result.name)
  return fileObject
}

export const displayRatingImpact = (impact: number) => {
  if (typeof impact !== 'number') {
    impact = 0
  }
  const impactString = impact > 0 ? `+${impact?.toFixed(3)}` : impact?.toFixed(3)
  return impactString.replace(/0\./, '.')
}

export const checkFloat = (number: number | null) => {
  let decimalCount = 0
  const numStr = String(number)
  if (numStr.includes('.')) {
    decimalCount = numStr.split('.')[1].length
  }
  return decimalCount <= MIN_DIGIT_AFTER_DECIMAL
}

export const getTextFromHtml = (html: string) => {
  const blocksFromHtml = convertFromHTML(html)
  return ContentState.createFromBlockArray(blocksFromHtml.contentBlocks, blocksFromHtml.entityMap).getPlainText('')
}

export const isObjectEmpty = (obj: any) => {
  return Object.values(obj).every((value) => value === '' || value === 0)
}

export const addSpaceAfterUppercase = (input: string, t: any): string => {
  const spacedString = input.replace(/([a-z])([A-Z])/g, '$1 $2')

  const result = spacedString.charAt(0).toUpperCase() + spacedString.slice(1)
  return t(`common.${result}`)
}

export const getRelativeDates = (dateInfo: string[], duration: string[], zone: string) => {
  const startDate = momenttz.tz(dateInfo?.[0], zone)
  const endDate = momenttz.tz(dateInfo?.[1], zone)
  const convertedStartDate = new Date(startDate as any)
  const convertedEndDate = new Date(endDate as any)
  const cStartValue = moment(convertedStartDate).format('MMMM DD')
  const cEndValue = moment(convertedEndDate).format('MMMM DD, YYYY')
  const oldStartDate = moment(duration?.[0]).format('MMMM DD')
  const oldEndDate = moment(duration?.[1]).format('MMMM DD, YYYY')

  return dateInfo?.length ? `${cStartValue} - ${cEndValue}` : `${oldStartDate} - ${oldEndDate}`
}

export const updatePhoneResendTime = (Endtime: any) => {
  const time1 = new Date(Endtime)
  const time2 = new Date()
  const dif = Math.round(Number(time1) - Number(time2)) / 1000
  return dif
}

export const updateEmailResendCount = () => {
  const previousCount = JSON.parse(localStorage.getItem(LOCAL_EMAIL_RESEND_COUNT) as any)
  localStorage.setItem(LOCAL_EMAIL_RESEND_COUNT, JSON.stringify(previousCount + 1))
}

export const createStaticMapWithLatLng = ({
  w = 400,
  lat,
  lng,
}: {
  w?: number
  h?: number
  lat: number
  lng: number
}) =>
  `https://maps.googleapis.com/maps/api/staticmap?zoom=16&size=${Math.floor(w) < 300 ? 300 : Math.floor(w)}x${
    Math.floor(w) < 300 ? Math.floor(300 * 0.7) : Math.floor(w * 0.7)
  }&maptype=roadmap&markers=color:red%7Clabel:S%7C${lat},${lng}&key=${GOOGLEMAP_API_KEY}`

export const createStaticEventMapWithLatLng = ({
  w,
  h,
  lat,
  lng,
}: {
  w: number
  h: number
  lat: number
  lng: number
}) =>
  `https://maps.googleapis.com/maps/api/staticmap?zoom=16&size=${w}x${h}&maptype=roadmap&markers=color:red%7Clabel:S%7C${lat},${lng}&key=${GOOGLEMAP_API_KEY}`

export const changeLanguage = (language: string) => {
  i18n.changeLanguage(language)
}

export const isUpperCase = (inputString: string): boolean => {
  return inputString === inputString.toUpperCase()
}

export const createFullURL = (domain: string) => {
  const regex = /^(http:\/\/|https:\/\/)/i
  const cleanedUrl = domain.replace(/,/g, '')

  if (!regex.test(cleanedUrl)) {
    if (!cleanedUrl.startsWith('www.')) {
      return `https://www.${cleanedUrl}`
    }
    return `https://${cleanedUrl}`
  }
  return cleanedUrl
}

export const parseLine = (line: string) => {
  const results: { type: string; content: string; id?: number }[] = []
  let lastIndex = 0

  const combinedRegex = new RegExp(`${regexWeb.source}|${regexHashTag.source}|${regexGetTagInContent.source}`, 'g')

  let match
  while ((match = combinedRegex.exec(line)) !== null) {
    if (lastIndex < match.index) {
      results.push({ type: 'text', content: line.substring(lastIndex, match.index) })
    }

    const [matchedText] = match

    if (regexWeb.test(matchedText)) {
      results.push({ type: 'web', content: matchedText })
    }

    if (regexHashTag.test(matchedText)) {
      results.push({ type: 'hashtag', content: matchedText })
    }

    if (regexGetTagInContent.test(matchedText)) {
      const contents = matchedText.match(regexGetTagInContent)
      const regex = /@\[([^\]]+)\]\((\d+)\)/
      contents?.forEach((item) => {
        const matchTag = item.match(regex)
        if (matchTag) {
          results.push({ type: 'tag', content: matchTag[1], id: parseInt(matchTag[2]) })
        }
      })
    }

    lastIndex = combinedRegex.lastIndex
  }

  if (lastIndex < line.length) {
    results.push({ type: 'text', content: line.substring(lastIndex) })
  }

  return results
}

export const convertTimeZone = (dateText: string) => {
  const date = moment(dateText)

  const year = date.year()
  const month = date.month()
  const day = date.date()

  return new Date(year, month, day)
}

export const convertDates = (dateArray: number[]) => {
  if (!dateArray || dateArray.length !== 5) {
    return null
  }
  const [year, month, day, hour, minute] = dateArray
  return new Date(year, month - 1, day, hour, minute)
}
